/**
 * GradeVoteSection Component
 *
 * A compact, mobile-first section for community grade voting.
 * Shows color grade range as quick-tap buttons with a dropdown for other grades.
 * Supports both V-scale and Font scale display.
 */

import { type CSSProperties } from 'react';
import {
  V_GRADES,
  type VGrade,
  type GradeModifier,
  type GradeDisplayPreference,
} from '@flash/shared';
import { useGradeSubmission } from '@/hooks/useGradeSubmission';
import { useRouteGradeSubmissions } from '@/hooks/useRouteGradeSubmissions';
import { GradeDistributionChart } from './GradeDistributionChart';
import { vToFont, fontToV, ALL_FONT_GRADES, FONT_TO_V_MAP } from '@/utils/gradeConversion';

/**
 * Color grade range from gym settings
 */
interface ColorGradeRange {
  min: VGrade;
  max: VGrade;
}

/**
 * Props for GradeVoteSection component
 */
interface GradeVoteSectionProps {
  /** Route ID to vote on */
  routeId: string;
  /** Official grade set by setters (null for color-only gyms) */
  officialGrade: VGrade | null;
  /** Official grade modifier */
  officialGradeModifier?: GradeModifier | null | undefined;
  /** Consensus grade calculated from community votes (null if not enough votes) */
  consensusGrade?: VGrade | null;
  /** Grade display preference ('v-scale' or 'font') */
  displayPreference?: GradeDisplayPreference;
  /** Color grade range from gym settings (for quick-tap buttons) */
  colorGradeRange?: ColorGradeRange | null;
}

/**
 * Format a grade for display based on scale preference
 */
const formatGradeDisplay = (
  grade: VGrade,
  modifier: GradeModifier | null | undefined,
  scale: 'v-scale' | 'font'
): string => {
  const baseGrade = scale === 'font' ? vToFont(grade) : grade;
  if (!modifier) return baseGrade;
  return `${baseGrade}${modifier}`;
};

/** Maximum number of quick-tap buttons for mobile-friendly UI */
const MAX_QUICK_SELECT_GRADES = 5;

/**
 * Get grades for quick selection based on color grade range or official grade
 * Limited to MAX_QUICK_SELECT_GRADES for mobile-friendly UI
 */
const getQuickSelectGrades = (
  colorGradeRange: ColorGradeRange | null | undefined,
  officialGrade: VGrade | null
): VGrade[] => {
  // Priority 1: Use color grade range if available
  if (colorGradeRange) {
    const minIndex = V_GRADES.indexOf(colorGradeRange.min);
    const maxIndex = V_GRADES.indexOf(colorGradeRange.max);
    if (minIndex !== -1 && maxIndex !== -1) {
      const rangeGrades = V_GRADES.slice(minIndex, maxIndex + 1);
      // Limit to MAX_QUICK_SELECT_GRADES for mobile-friendliness
      return rangeGrades.slice(0, MAX_QUICK_SELECT_GRADES);
    }
  }

  // Priority 2: Use grades around official grade
  if (officialGrade) {
    const index = V_GRADES.indexOf(officialGrade);
    const startIndex = Math.max(0, index - 2);
    const endIndex = Math.min(V_GRADES.length - 1, index + 2);
    return V_GRADES.slice(startIndex, endIndex + 1);
  }

  // Fallback: Show beginner grades
  return ['VB', 'V0', 'V1', 'V2', 'V3'];
};

/**
 * Get Font grades that correspond to a V-grade range
 * Limited to MAX_QUICK_SELECT_GRADES for mobile-friendly UI
 */
const getFontGradesInRange = (minVGrade: VGrade, maxVGrade: VGrade): string[] => {
  const minIndex = V_GRADES.indexOf(minVGrade);
  const maxIndex = V_GRADES.indexOf(maxVGrade);

  const fontGrades = ALL_FONT_GRADES.filter((fontGrade) => {
    const vGrade = FONT_TO_V_MAP[fontGrade];
    if (!vGrade) return false;
    const vIndex = V_GRADES.indexOf(vGrade);
    return vIndex >= minIndex && vIndex <= maxIndex;
  });

  // Limit to MAX_QUICK_SELECT_GRADES for mobile-friendliness
  return fontGrades.slice(0, MAX_QUICK_SELECT_GRADES);
};

/**
 * GradeVoteSection - Compact community grade voting
 */
export const GradeVoteSection = ({
  routeId,
  officialGrade,
  officialGradeModifier,
  consensusGrade,
  displayPreference = 'v-scale',
  colorGradeRange,
}: GradeVoteSectionProps) => {
  const scale = displayPreference === 'font' ? 'font' : 'v-scale';

  const {
    submission: userSubmission,
    submitGrade,
    removeGrade,
    isSubmitting,
  } = useGradeSubmission(routeId);

  const { distribution, minGrade, maxGrade, totalCount } = useRouteGradeSubmissions(routeId);

  const userGrade = userSubmission?.grade ?? null;
  const quickSelectGrades = getQuickSelectGrades(colorGradeRange, officialGrade);

  // For Font scale, get the Font grades that map to the quick select V-grades
  const quickSelectFontGrades =
    scale === 'font' && quickSelectGrades.length > 0
      ? getFontGradesInRange(
          quickSelectGrades[0]!,
          quickSelectGrades[quickSelectGrades.length - 1]!
        )
      : [];

  const handleSelectGrade = async (grade: VGrade) => {
    if (isSubmitting) return;
    if (grade === userGrade) {
      await removeGrade();
    } else {
      await submitGrade(grade);
    }
  };

  /**
   * Handle Font grade selection - converts to V-grade before submitting.
   */
  const handleSelectFontGrade = async (fontGrade: string) => {
    if (isSubmitting) return;
    const vGrade = fontToV(fontGrade);
    if (vGrade === userGrade) {
      await removeGrade();
    } else {
      await submitGrade(vGrade);
    }
  };

  /**
   * Handle dropdown selection
   */
  const handleDropdownChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value || isSubmitting) return;

    if (scale === 'font') {
      await handleSelectFontGrade(value);
    } else {
      await handleSelectGrade(value as VGrade);
    }
  };

  // Check if user's vote is outside the quick-select range (to show in dropdown)
  const userVoteOutsideRange = userGrade && !quickSelectGrades.includes(userGrade);

  return (
    <div style={containerStyle}>
      {/* Compact header */}
      <div style={headerStyle}>
        <span style={titleStyle}>Grade it</span>
        {officialGrade && (
          <span style={setterGradeStyle}>
            Setter: {formatGradeDisplay(officialGrade, officialGradeModifier, scale)}
          </span>
        )}
        {userGrade && (
          <button onClick={removeGrade} disabled={isSubmitting} style={yourVoteBadgeStyle}>
            You: {formatGradeDisplay(userGrade, null, scale)} ✕
          </button>
        )}
      </div>

      {/* Quick-tap grade buttons + dropdown */}
      <div style={buttonsRowStyle}>
        {scale === 'font'
          ? // Font scale: show Font grades in range
            quickSelectFontGrades.map((fontGrade) => {
              const mappedVGrade = fontToV(fontGrade);
              const isSelected = mappedVGrade === userGrade;
              return (
                <button
                  key={fontGrade}
                  onClick={() => handleSelectFontGrade(fontGrade)}
                  disabled={isSubmitting}
                  style={{
                    ...chipStyle,
                    ...(isSelected ? chipSelectedStyle : {}),
                  }}
                  aria-pressed={isSelected}
                >
                  {fontGrade}
                </button>
              );
            })
          : // V-scale: show V-grades in range
            quickSelectGrades.map((grade) => {
              const isSelected = grade === userGrade;
              const isOfficial = grade === officialGrade;

              return (
                <button
                  key={grade}
                  onClick={() => handleSelectGrade(grade)}
                  disabled={isSubmitting}
                  style={{
                    ...chipStyle,
                    ...(isSelected ? chipSelectedStyle : {}),
                    ...(isOfficial && !isSelected ? chipOfficialStyle : {}),
                  }}
                  aria-pressed={isSelected}
                >
                  {formatGradeDisplay(grade, null, scale)}
                </button>
              );
            })}

        {/* Dropdown for other grades */}
        <select
          value={userVoteOutsideRange ? (scale === 'font' ? vToFont(userGrade!) : userGrade!) : ''}
          onChange={handleDropdownChange}
          disabled={isSubmitting}
          style={{
            ...dropdownStyle,
            ...(userVoteOutsideRange ? dropdownSelectedStyle : {}),
          }}
          aria-label="Select other grade"
        >
          <option value="">Other</option>
          {scale === 'font'
            ? ALL_FONT_GRADES.filter((fg) => !quickSelectFontGrades.includes(fg)).map(
                (fontGrade) => (
                  <option key={fontGrade} value={fontGrade}>
                    {fontGrade}
                  </option>
                )
              )
            : V_GRADES.filter((g) => !quickSelectGrades.includes(g)).map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
        </select>
      </div>

      {/* Compact distribution */}
      {totalCount > 0 && (
        <GradeDistributionChart
          distribution={distribution}
          minGrade={minGrade}
          maxGrade={maxGrade}
          totalCount={totalCount}
          officialGrade={officialGrade}
          consensusGrade={consensusGrade}
          userGrade={userGrade}
          displayPreference={displayPreference}
        />
      )}

      {/* Empty state - very compact */}
      {totalCount === 0 && <p style={emptyHintStyle}>Be first to vote!</p>}
    </div>
  );
};

/**
 * Compact mobile-first styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
};

const titleStyle: CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 700,
  color: 'var(--color-text-primary, #1f2937)',
};

const setterGradeStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
  padding: '2px 6px',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  borderRadius: '4px',
};

const yourVoteBadgeStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#3b82f6',
  padding: '2px 6px',
  backgroundColor: '#eff6ff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginLeft: 'auto',
};

const buttonsRowStyle: CSSProperties = {
  display: 'flex',
  gap: '4px',
  flexWrap: 'wrap',
  alignItems: 'center',
};

const chipStyle: CSSProperties = {
  padding: '6px 10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '6px',
  backgroundColor: 'var(--color-bg-primary, white)',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
  minWidth: '36px',
  textAlign: 'center',
};

const chipSelectedStyle: CSSProperties = {
  backgroundColor: '#3b82f6',
  borderColor: '#3b82f6',
  color: 'white',
};

const chipOfficialStyle: CSSProperties = {
  borderColor: '#3b82f6',
  borderWidth: '2px',
};

const dropdownStyle: CSSProperties = {
  padding: '6px 8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '6px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  color: 'var(--color-text-secondary, #6b7280)',
  cursor: 'pointer',
  minWidth: '60px',
};

const dropdownSelectedStyle: CSSProperties = {
  backgroundColor: '#3b82f6',
  borderColor: '#3b82f6',
  color: 'white',
};

const emptyHintStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.75rem',
  color: 'var(--color-text-tertiary, #9ca3af)',
  fontStyle: 'italic',
};

export default GradeVoteSection;
